import React, { useState, useEffect } from 'react';
import * as PR from "../../../../prime-react/index";
import "./GraphAndStats.scss";
import { getGraphConfig } from './GraphConfig';
import { currencyFormat } from '../../../../utils/reuse';
import EventMetaTable from './EventMetaTable';

const GamingRevenueGraphAndStats = ({ betData, loading, groupId }) => {
  const { stackedOptions } = getGraphConfig();
  const [activeIndex, setActiveIndex] = useState(0);
  const [chartData, setChartData] = useState({});
  const [stackedOptionsData] = useState(stackedOptions);
  const [chartOptions, setChartOptions] = useState(stackedOptions);
  const [selectedGraph, setSelectedGraph] = useState({});
  const [dataList, setDataList] = useState([]);
  const [tabLabels] = useState(['events', 'tournaments', 'sports']);
  const [totals, setTotals] = useState({});

  const getSingleChartHandler = (event, i) => {
    if (i.length > 0) {
      const index = i[0].index;
      const datasetIndex = i[0].datasetIndex;
      const sectionData = chartData.datasets[datasetIndex];
      const graphId = sectionData.id[index]
      setSelectedGraph({ graphId, sectionId: sectionData.sectionId })
    }
  }
  chartOptions.onClick = getSingleChartHandler;
  const footerGroup = (
    <PR.ColumnGroup>
      <PR.Row>
        <PR.Column footer={totals?.name} style={{ width: "40%" }} />
        <PR.Column footer={currencyFormat(totals?.totalStake, groupId)} />
        <PR.Column footer={currencyFormat(totals?.netGGR, groupId)} />
      </PR.Row>
    </PR.ColumnGroup>
  );

  useEffect(() => {
    const tabName = tabLabels[activeIndex];
    const totalData = betData[tabName]?.total ? betData[tabName]?.total : {}
    let tabData = betData[tabName]?.betSummary?.length ? betData[tabName]?.betSummary : []
       tabData = tabData.map((b, index) => {
      b.id = index + 1;
      b.netGGRFormat = currencyFormat(b.netGGR, groupId);
      b.totalStakeFormat = currencyFormat(b.totalStake, groupId);
      return b;
    });
    setSelectedGraph({});
    setDataList(tabData);
    setTotals(totalData);
  }, [betData, activeIndex, tabLabels, groupId]);
  useEffect(() => {
    const dataListSlice = dataList?.sort((a, b)=>  b.netGGR - a.netGGR).slice(0, 20);
    const data = {
      labels: dataListSlice.map(d => d.name),
      datasets: [
        {
          label: 'Total Stake',
          sectionId: 'totalStake',
          backgroundColor: '#FFCD02',
          data: dataListSlice.map(d => d.totalStake),
          id: dataListSlice.map(d => d.id),
          borderRadius: 2,
          barThickness: 25,
          type: 'bar',
          borderWidth: dataListSlice.map(d =>
            d.id === selectedGraph?.graphId ? 1 : 0
          ),
          borderColor: dataListSlice.map(d =>
            d.id === selectedGraph?.graphId ? 'blue' : '#FFCD02'
          ),
        },
        {
          label: 'Net GGR',
          sectionId: 'netGGR',
          id: dataListSlice.map(d => d.id),
          backgroundColor: '#000000',
          data: dataListSlice.map(d => d.netGGR),
          borderRadius: 2,
          barThickness: 25,
          borderWidth: dataListSlice.map(d =>
            d.id === selectedGraph?.graphId ? 1 : 0
          ),
          type: 'bar',
          borderColor: dataListSlice.map(d =>
            d.id === selectedGraph?.graphId ? 'blue' : '#000000'
          ),
        }
      ]
    };
    const totalList = dataListSlice.map(d => d.totalStake + d.netGGR)
    const allValues = totalList.filter(v => typeof v === 'number' && !isNaN(v));
    let minValue, maxValue;
    if (allValues.length === 0) {
      minValue = -1000;
      maxValue = 1000;
    } else {
      const rawMin = Math.min(...allValues);
      const rawMax = Math.max(...allValues);
      const absMax = Math.max(Math.abs(rawMin), Math.abs(rawMax));
      const roundValue = Math.round(absMax);  
      const digits = Math.pow(10, roundValue.toString().length - 1);
      const roundedMax = Math.ceil(roundValue / digits) * digits;
      minValue = -roundedMax;
      maxValue = roundedMax;
    }
    const options = { ...stackedOptionsData }; // clone to avoid side effects if needed
    options.scales.y.min = minValue;
    options.scales.y.max = maxValue;
    options.scales.y.ticks = {
      ...options.scales.y.ticks,
      callback: value => currencyFormat(value, groupId, 0)
    };
    options.plugins.tooltip = {
      ...options.plugins.tooltip,
      callbacks: {
        label: context => `${context.dataset.label}: ${currencyFormat(context.parsed.y, groupId)}`
      }
    };
    setChartData(data);
    setChartOptions(options)
  }, [dataList, selectedGraph, stackedOptionsData, groupId]);


  return (
    <>
      <h2 className="heading-h2 mt-3">Single Bets</h2>
      <section className="graph-stats-section">
        <div className="grid">
          <div className="col-12 md:col-7">
            <h2>Total Stake & Net GGR</h2>
            <div className="card mt-5 custom-height">
              {loading ? (
                <div className='loading-block'>
                  <PR.ProgressSpinner />
                </div>
              ) : (
                <PR.Chart type="bar" data={chartData} options={chartOptions} style={{ height: '100%' }} />
              )}
            </div>
          </div>
          <div className="col-12 md:col-5">
            <PR.TabView className={`stats-tab-section ${!loading ? 'mt-1' : 'loading-tab mt-0'}`} activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
              <PR.TabPanel header="Event">
                {tabLabels[activeIndex] === 'events' && 
                loading ? (
                    <div className="loading-block">
                      <PR.ProgressSpinner />
                    </div>
                  ) : (
                    <EventMetaTable dataList={dataList} footerGroup={footerGroup} header="Events" graphId={selectedGraph?.graphId} />
                  )
                }
              </PR.TabPanel>
              <PR.TabPanel header="Tournament/League">
              {tabLabels[activeIndex] === 'tournaments' && 
                loading ? (
                    <div className="loading-block">
                      <PR.ProgressSpinner />
                    </div>
                  ) : (
                <EventMetaTable dataList={dataList} footerGroup={footerGroup} header={'Tournament/League'} graphId={selectedGraph?.graphId}></EventMetaTable>
                )
              }
              </PR.TabPanel>
              <PR.TabPanel header="Sport">
                {tabLabels[activeIndex] === 'sports' && 
                loading ? (
                    <div className="loading-block">
                      <PR.ProgressSpinner />
                    </div>
                  ) : (
                  <EventMetaTable dataList={dataList} footerGroup={footerGroup} header={'Sport'} graphId={selectedGraph?.graphId} ></EventMetaTable>
                  )
                }
              </PR.TabPanel>
            </PR.TabView>
          </div>
        </div>
      </section>
    </>
  );
};

export default GamingRevenueGraphAndStats;
